/**
 * Volume API Service
 *
 * API methods for volume operations including creation, access grants, and file operations.
 * Updated to use standardized API response format.
 */

import apiClient from "./client";
import type { ApiSuccessResponse, ApiCollectionResponse, ApiActionResponse } from "../types/api.types";
import type {
  VolumeSanitized,
  VolumeCreateDTO,
  VolumeUpdateDTO,
  VolumeListQuery,
  PresignedUrlRequest,
  PresignedUrlResponse,
  ObjectListQuery,
  ObjectListResponse,
  VolumeObject,
} from "../types/volume.types";

// ============================================================================
// VOLUME SERVICE
// ============================================================================

export const volumeService = {
  /**
   * List all volume resources for a tenant
   */
  listVolumes: async (tenantId: string, query?: VolumeListQuery): Promise<VolumeSanitized[]> => {
    const response = await apiClient.get<ApiCollectionResponse<VolumeSanitized>>(`/tenants/${tenantId}/volumes`, {
      params: query,
    });
    return response.data.data;
  },

  /**
   * Get volume by ID
   */
  getVolume: async (tenantId: string, volumeId: string): Promise<VolumeSanitized> => {
    const response = await apiClient.get<ApiSuccessResponse<VolumeSanitized>>(`/tenants/${tenantId}/volumes/${volumeId}`);
    return response.data.data;
  },

  /**
   * Create a new volume resource
   */
  createVolume: async (tenantId: string, data: VolumeCreateDTO): Promise<VolumeSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<VolumeSanitized>>(`/tenants/${tenantId}/volumes`, data);
    return response.data.data;
  },

  /**
   * Update volume
   */
  updateVolume: async (tenantId: string, volumeId: string, data: VolumeUpdateDTO): Promise<VolumeSanitized> => {
    const response = await apiClient.put<ApiSuccessResponse<VolumeSanitized>>(`/tenants/${tenantId}/volumes/${volumeId}`, data);
    return response.data.data;
  },

  /**
   * Delete volume
   */
  deleteVolume: async (tenantId: string, volumeId: string): Promise<{ message: string }> => {
    const response = await apiClient.delete<ApiActionResponse>(`/tenants/${tenantId}/volumes/${volumeId}`);
    return { message: response.data.message };
  },

  /**
   * List objects in volume
   */
  listObjects: async (tenantId: string, volumeId: string, query?: ObjectListQuery): Promise<ObjectListResponse> => {
    const response = await apiClient.get<ApiSuccessResponse<ObjectListResponse>>(`/tenants/${tenantId}/volumes/${volumeId}/objects`, {
      params: query,
    });
    return response.data.data;
  },

  /**
   * Get presigned URL for file upload/download
   */
  getPresignedUrl: async (tenantId: string, volumeId: string, data: PresignedUrlRequest): Promise<PresignedUrlResponse> => {
    const response = await apiClient.post<ApiSuccessResponse<PresignedUrlResponse>>(`/tenants/${tenantId}/volumes/${volumeId}/presigned-url`, data);
    return response.data.data;
  },

  /**
   * Delete object from volume
   */
  deleteObject: async (tenantId: string, volumeId: string, objectKey: string): Promise<{ message: string }> => {
    const response = await apiClient.delete<ApiActionResponse>(
      `/tenants/${tenantId}/volumes/${volumeId}/objects/${encodeURIComponent(objectKey)}`
    );
    return { message: response.data.message };
  },

  /**
   * Batch delete objects from volume
   */
  batchDeleteObjects: async (tenantId: string, volumeId: string, objectKeys: string[]): Promise<{ message: string; deleted: number }> => {
    const response = await apiClient.post<ApiActionResponse>(`/tenants/${tenantId}/volumes/${volumeId}/objects/batch-delete`, {
      objectKeys,
    });
    return { message: response.data.message, deleted: (response.data.data?.deleted as number) || 0 };
  },

  /**
   * Copy object within volume
   */
  copyObject: async (tenantId: string, volumeId: string, sourceKey: string, destinationKey: string): Promise<VolumeObject> => {
    const response = await apiClient.post<ApiSuccessResponse<VolumeObject>>(`/tenants/${tenantId}/volumes/${volumeId}/objects/copy`, {
      sourceKey,
      destinationKey,
    });
    return response.data.data;
  },

  /**
   * Get object metadata
   */
  getObjectMetadata: async (tenantId: string, volumeId: string, objectKey: string): Promise<VolumeObject> => {
    const response = await apiClient.get<ApiSuccessResponse<VolumeObject>>(
      `/tenants/${tenantId}/volumes/${volumeId}/objects/${encodeURIComponent(objectKey)}/metadata`
    );
    return response.data.data;
  },
};

export default volumeService;
